import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { AppHttpHeadersService } from '@service/rest/headers/app-http-headers.service';
import { CalendarEventDetailRequest } from '@model/calendar/calendar';

@Injectable({
  providedIn: 'root',
})
export class CalendarService {
  constructor(
    private http: HttpClient,
    private appHttpHeadersService: AppHttpHeadersService
  ) { }
  /**
   * Gets calendars
   */
  getCalendars(): Observable<any> {
    return this.http.get(environment.gateway_api + '/calendars', { headers: this.appHttpHeadersService.getHeaders() });
  }

  shareCalendarsWithOthers(formData): Observable<any> {
    return this.http.post<any>(environment.gateway_api + '/calendars/share-calendar', formData, { headers: this.appHttpHeadersService.getHeaders() });
  }

  requestCalendarAccess(formData): Observable<any> {
    return this.http.post<any>(environment.gateway_api + '/calendars/share-calendar/request', formData, {
      headers: this.appHttpHeadersService.getHeaders(),
    });
  }

  getCalendarsSharedWithOthers(): Observable<any> {
    return this.http.get(environment.gateway_api + '/calendars/share-calendar', { headers: this.appHttpHeadersService.getHeaders() });
  }

  removeMemberFromSharedCalendar(member: string): Observable<any> {
    return this.http.delete(environment.gateway_api + '/calendars/share-calendar/' + member, { headers: this.appHttpHeadersService.getHeaders() });
  }

  getCalendarsSharedWithUser(onlyHavePermissionToSendMeeting: 0 | 1 = 0): Observable<any> {
    return this.http.get(
      environment.gateway_api + '/calendars/shared-calendar-owners?only_have_permission_to_send_meeting=' + onlyHavePermissionToSendMeeting,
      { headers: this.appHttpHeadersService.getHeaders() }
    );
  }

  getOpenCalendars(): Observable<any> {
    return this.http.get(environment.gateway_api + '/calendars/open-calendar', { headers: this.appHttpHeadersService.getHeaders() });
  }

  addOpenCalendar(formData): Observable<any> {
    return this.http.post(environment.gateway_api + '/calendars/open-calendar', formData, { headers: this.appHttpHeadersService.getHeaders() });
  }

  removeOpenCalendar(formData): Observable<any> {
    const options = {
      headers: this.appHttpHeadersService.getHeaders(),
      body: formData,
    };
    return this.http.delete(environment.gateway_api + '/calendars/open-calendar', options);
  }

  /**
   * Gets appointment list
   * @param id
   * @param startTime
   * @param endTime
   */
  getCalendarAppointmentList(id: string, startTime?: number, endTime?: number): Observable<any> {
    let url = environment.gateway_api + '/calendars/appointment/' + id + '/list';
    if (startTime && endTime) {
      url = url + '?start_time=' + startTime + '&end_time=' + endTime;
    }
    return this.http.post(url, {}, { headers: this.appHttpHeadersService.getHeaders() });
  }

  getOpenCalendarAppointmentList(owner_emails: string[], calendarId: string, startTime?: number, endTime?: number): Observable<any> {
    let url = environment.gateway_api + '/calendars/appointments/list?calendar_id=' + calendarId;
    if (startTime && endTime) {
      url = url + '&start_time=' + startTime + '&end_time=' + endTime;
    }
    const formData = {
      owner_emails: [...owner_emails],
    };
    return this.http.post(url, formData, { headers: this.appHttpHeadersService.getHeaders() });
  }

  /**
   * Gets appointment detail
   * @param appointmentId
   * @param recur_start_time
   * @param recur_end_time
   * @param recur_date
   */
  public getCalendarEventDetail(eventDetailRequest: CalendarEventDetailRequest): Observable<any> {
    let queryParams = '?id=' + eventDetailRequest.appointmentId;
    if (eventDetailRequest.ownerEmail) {
      queryParams = queryParams + '&owner_email=' + eventDetailRequest.ownerEmail;
    }
    if (eventDetailRequest.recur_start_time && eventDetailRequest.recur_end_time && eventDetailRequest.recur_date) {
      queryParams =
        queryParams +
        '&recur_start_time=' +
        eventDetailRequest.recur_start_time +
        '&recur_end_time=' +
        eventDetailRequest.recur_end_time +
        '&recur_date=' +
        eventDetailRequest.recur_date;
      if (eventDetailRequest.except_id_raw) {
        queryParams = queryParams + '&except_id_raw=' + eventDetailRequest.except_id_raw;
      }
      return this.http.get(environment.gateway_api + '/calendars/appointment' + queryParams + '&convertAllDayEndDate=true', {
        headers: this.appHttpHeadersService.getHeaders(),
      });
    } else {
      return this.http.get(environment.gateway_api + '/calendars/appointment' + queryParams + '&convertAllDayEndDate=true', {
        headers: this.appHttpHeadersService.getHeaders(),
      });
    }
  }

  public getCalendarEventMetadata(uid: string, ownerEmail: string): Observable<any> {
    const queryParams = '?uid=' + uid + '&owner_email=' + ownerEmail;
    return this.http.get(environment.gateway_api + '/calendars/appointments/meta-data' + queryParams, {
      headers: this.appHttpHeadersService.getHeaders(),
    });
  }

  /**
   * Answers invitation
   * @param formData
   */
  answerInvitation(formData): Observable<any> {
    return this.http.post<any>(environment.gateway_api + '/emails/answer-invitation', formData, {
      headers: this.appHttpHeadersService.getV2AuthHeaders(),
    });
  }
  /**
   * Creates appointment
   * @param formData
   */
  createAppointment(formData): Observable<any> {
    return this.http.post<any>(environment.gateway_api + '/calendars/appointment/create', formData, {
      headers: this.appHttpHeadersService.getHeaders(),
    });
  }
  /**
   * Updates appointment
   * @param formData
   */
  updateAppointment(formData): Observable<any> {
    return this.http.post<any>(environment.gateway_api + '/calendars/appointment/update', formData, {
      headers: this.appHttpHeadersService.getHeaders(),
    });
  }
  /**
   * Cancels appointment
   * @param formData
   */
  cancelAppointment(formData): Observable<any> {
    return this.http.post<any>(environment.gateway_api + '/calendars/appointment/cancel', formData, {
      headers: this.appHttpHeadersService.getHeaders(),
    });
  }

  forwardAppointment(formData): Observable<any> {
    return this.http.post<any>(environment.gateway_api + '/calendars/appointment/forward', formData, {
      headers: this.appHttpHeadersService.getHeaders(),
    });
  }

  getSchedulingEvents(email: string, startTime: number, endTime: number): Observable<any> {
    return this.http.get(
      environment.gateway_api + '/calendars/appointment/schedule?email=' + email + '&start_time=' + startTime + '&end_time=' + endTime,
      { headers: this.appHttpHeadersService.getHeaders() }
    );
  }

  proposeNewTime(formData): Observable<any> {
    return this.http.post<any>(environment.gateway_api + '/calendars/appointment/propose-new-time', formData, {
      headers: this.appHttpHeadersService.getHeaders(),
    });
  }

  snoozeAlarm(formData): Observable<any> {
    return this.http.post<any>(environment.gateway_api + '/calendars/appointment/snooze-alarm', formData, {
      headers: this.appHttpHeadersService.getHeaders(),
    });
  }

  dismissAlarm(formData): Observable<any> {
    return this.http.post<any>(environment.gateway_api + '/calendars/appointment/dismiss-alarm', formData, {
      headers: this.appHttpHeadersService.getHeaders(),
    });
  }

  getAlarms(calendar_id: number, start_time: number, end_time: number): Observable<any> {
    return this.http.get(
      environment.gateway_api + '/calendars/appointment/alarms?calendar_id=' + calendar_id + '&start_time=' + start_time + '&end_time=' + end_time,
      { headers: this.appHttpHeadersService.getHeaders() }
    );
  }
}
